import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { queryClient } from "@finitless/shared/lib";
import { useApiStore } from "@finitless/shared/stores";
import {
  mapUiLocaleToApiLanguage,
  normalizeLocale,
  isRtlLocale,
} from "../i18n/config";

export const useSyncApiLanguageWithQueries = () => {
  const { i18n } = useTranslation("app");
  const setApiLanguage = useApiStore((state) => state.setApiLanguage);
  const previousApiLanguageRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    const syncLanguageAndQueries = async () => {
      const uiLocale = normalizeLocale(i18n.resolvedLanguage || i18n.language);
      const mappedApiLanguage = mapUiLocaleToApiLanguage(uiLocale);

      if (typeof document !== "undefined") {
        document.documentElement.lang = uiLocale;
        document.documentElement.dir = isRtlLocale(uiLocale) ? "rtl" : "ltr";
      }

      setApiLanguage(mappedApiLanguage);

      const previousApiLanguage = previousApiLanguageRef.current;
      const isFirstRender = previousApiLanguage === null;
      const hasApiLanguageChanged =
        !isFirstRender && previousApiLanguage !== mappedApiLanguage;

      previousApiLanguageRef.current = mappedApiLanguage;

      if (!hasApiLanguageChanged) {
        return;
      }

      await queryClient.invalidateQueries({ refetchType: "none" });
      if (isCancelled) {
        return;
      }

      await queryClient.refetchQueries({ type: "active" });
    };

    syncLanguageAndQueries();

    return () => {
      isCancelled = true;
    };
  }, [i18n.language, i18n.resolvedLanguage, setApiLanguage]);
};

export default useSyncApiLanguageWithQueries;
